import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  CUSTOMER_STATUSES,
  CUSTOMER_TIERS,
  createCustomer,
  updateCustomer,
  type CustomerRead,
} from '@/features/customers/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const baseSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(255, 'That name is too long'),
  email: z
    .string()
    .trim()
    .max(255)
    .refine((v) => v === '' || /\S+@\S+\.\S+/.test(v), {
      message: 'That is not a valid email address',
    }),
  phone: z.string().trim().max(30, 'That phone number is too long'),
  address_line1: z.string().trim().max(255),
  address_line2: z.string().trim().max(255),
  city: z.string().trim().max(100),
  // Tax-regime-aware -- a GSTIN's embedded state code is cross-checked against this on the
  // server, and neither field can be validated correctly here without duplicating that
  // regime logic client-side, so both stay free text and the server's own message wins.
  state: z.string().trim().max(50),
  postal_code: z.string().trim().max(20),
  country: z.string().trim().max(100),
  tax_id: z.string().trim().max(50),
  tax_exempt: z.boolean(),
  credit_limit: z
    .string()
    .trim()
    .refine((v) => v === '' || Number.isFinite(Number(v)), { message: 'Enter a number' })
    .refine((v) => v === '' || Number(v) >= 0, { message: 'Cannot be negative' }),
  payment_terms_days: z
    .string()
    .trim()
    .refine((v) => v === '' || Number.isInteger(Number(v)), { message: 'Enter a whole number' })
    .refine((v) => v === '' || (Number(v) >= 0 && Number(v) <= 365), {
      message: 'Between 0 and 365 days',
    }),
  notes: z.string().trim().max(4000),
  tier: z.enum(CUSTOMER_TIERS),
  status: z.enum(CUSTOMER_STATUSES),
})

const createExtra = z.object({
  code: z.string().trim().min(1, 'Enter a customer code').max(50, 'That code is too long'),
})

type FormValues = z.infer<typeof baseSchema> & Partial<z.infer<typeof createExtra>>

/**
 * Create and edit in one dialog. `assigned_rep_id` deliberately does not appear here --
 * the router gates handing an account to another rep on `customers:delete`, not `:update`
 * (see `reassignCustomer`), so that one field has its own control on the list/detail
 * screen instead of living in a form most editors cannot fully submit.
 */
export function CustomerFormDialog({
  open,
  customer,
  draft,
  onClose,
}: {
  open: boolean
  /** Absent for a new customer. */
  customer?: CustomerRead
  /** Starting values for a new customer -- a nearby lead being signed up, say. */
  draft?: { name?: string; phone?: string; address_line1?: string; notes?: string }
  onClose: () => void
}) {
  const isEdit = customer !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(isEdit ? baseSchema : baseSchema.merge(createExtra)),
    defaultValues: {
      code: customer?.code ?? '',
      name: customer?.name ?? draft?.name ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? draft?.phone ?? '',
      address_line1: customer?.address_line1 ?? draft?.address_line1 ?? '',
      address_line2: customer?.address_line2 ?? '',
      city: customer?.city ?? '',
      state: customer?.state ?? '',
      postal_code: customer?.postal_code ?? '',
      country: customer?.country ?? 'US',
      tax_id: customer?.tax_id ?? '',
      tax_exempt: customer?.tax_exempt ?? false,
      credit_limit: customer?.credit_limit ?? '',
      payment_terms_days:
        customer?.payment_terms_days != null ? String(customer.payment_terms_days) : '30',
      notes: customer?.notes ?? draft?.notes ?? '',
      tier: (customer?.tier as (typeof CUSTOMER_TIERS)[number]) ?? 'standard',
      status: (customer?.status as (typeof CUSTOMER_STATUSES)[number]) ?? 'active',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const shared = {
        name: values.name,
        email: values.email || undefined,
        phone: values.phone || undefined,
        address_line1: values.address_line1 || undefined,
        address_line2: values.address_line2 || undefined,
        city: values.city || undefined,
        state: values.state || undefined,
        postal_code: values.postal_code || undefined,
        country: values.country || undefined,
        tax_id: values.tax_id || undefined,
        tax_exempt: values.tax_exempt,
        credit_limit: values.credit_limit || undefined,
        payment_terms_days: values.payment_terms_days
          ? Number(values.payment_terms_days)
          : undefined,
        notes: values.notes || undefined,
        tier: values.tier,
        status: values.status,
      }
      if (customer) return updateCustomer(customer.id, shared)
      return createCustomer({ ...shared, code: values.code! })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      void queryClient.invalidateQueries({ queryKey: ['customer', saved.id] })
      toast.success(isEdit ? `Saved ${saved.name}.` : `Created ${saved.name}.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.status === 409
            ? 'A customer with that code already exists.'
            : error.message
          : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${customer.name}` : 'New customer'}
        description={
          isEdit
            ? 'Tax ID and state are checked against the active tax regime on save.'
            : 'Added to the account list as soon as you save.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="customer-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Create customer'}
            </Button>
          </>
        }
      >
        <form id="customer-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          {!isEdit && (
            <Field label="Code" error={errors.code?.message}>
              {(props) => <Input {...props} {...register('code')} autoComplete="off" />}
            </Field>
          )}

          <Field label="Name" error={errors.name?.message}>
            {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" error={errors.email?.message} hint="Optional.">
              {(props) => (
                <Input {...props} {...register('email')} type="email" autoComplete="off" />
              )}
            </Field>
            <Field label="Phone" error={errors.phone?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('phone')} type="tel" autoComplete="off" />}
            </Field>
          </div>

          <Field label="Address line 1" error={errors.address_line1?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('address_line1')} autoComplete="off" />}
          </Field>
          <Field label="Address line 2" error={errors.address_line2?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('address_line2')} autoComplete="off" />}
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field label="City" error={errors.city?.message}>
              {(props) => <Input {...props} {...register('city')} autoComplete="off" />}
            </Field>
            <Field label="State" error={errors.state?.message}>
              {(props) => <Input {...props} {...register('state')} autoComplete="off" />}
            </Field>
            <Field label="Postal code" error={errors.postal_code?.message}>
              {(props) => <Input {...props} {...register('postal_code')} autoComplete="off" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Country" error={errors.country?.message}>
              {(props) => <Input {...props} {...register('country')} autoComplete="off" />}
            </Field>
            <Field
              label="Tax ID"
              error={errors.tax_id?.message}
              hint="GSTIN or resale certificate, per the active tax regime."
            >
              {(props) => <Input {...props} {...register('tax_id')} autoComplete="off" />}
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox {...register('tax_exempt')} />
            Tax exempt
          </label>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Credit limit" error={errors.credit_limit?.message} hint="Optional.">
              {(props) => (
                <Input
                  {...props}
                  {...register('credit_limit')}
                  inputMode="decimal"
                  className="tabular-nums"
                />
              )}
            </Field>
            <Field label="Payment terms (days)" error={errors.payment_terms_days?.message}>
              {(props) => (
                <Input {...props} {...register('payment_terms_days')} inputMode="numeric" />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Tier" error={errors.tier?.message}>
              {(props) => (
                <select {...props} {...register('tier')} className={SELECT_CLASS}>
                  {CUSTOMER_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier[0].toUpperCase() + tier.slice(1)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Status" error={errors.status?.message}>
              {(props) => (
                <select {...props} {...register('status')} className={SELECT_CLASS}>
                  {CUSTOMER_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status[0].toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <Field label="Notes" error={errors.notes?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('notes')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
